import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    handleGetAgentsData,
    resetState,
    setOrderByDirection,
    setOrderByProperty,
    resetScroll,
    getModalSwitch,
    gridCheckAll
} from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    provisionStateSelectData,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    passlyFormatDate,
    TIME_DATE_FORMAT
} from '@app/helpers';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { IAgentDetail, ITableHead, IFabMenu, IListPageBarEnabled, IActiveFilter, FilterEnum, IDotMenu, OrderType, Order } from '@app/models';
import { hasOrgPermission } from '@app/helpers/permissions';
import { ManageSystem } from '@app/store/constants';
import { basicService, agentsService } from '@app/services';
import { FabMenu, IModalData, ModalComponent, PasslyTable } from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import AddAgentDialog, { AgentViewType } from './dialogs/addAgent.dialog';
import { TableCell } from '@material-ui/core';
import { PasslyTooltip } from '@app/componentsV2';

interface IAgentsTableData {
    checked: boolean;
    agentName: string;
    agentType: string;
    provisioningState: string;
    lastModified: Date;
    id: string;
    iconUrl: string;
}

enum TableHeadEnum {
    agentName = 'agentName',
    agentType = 'agentType',
    provisioningState = 'provisioningState',
    lastModified = 'lastModified'
}

const AuthAgentsPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.AuthManagerAgents);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('AgentName');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IAgentsTableData[]>([]);

    const agents = useSelector((state: any) => state.agents.agents);
    const agentsCount = useSelector((state: any) => state.agents.count);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: true,
        actionMenuEnabled: hasOrgPermission([ManageSystem])
    };

    const editRow = (e: any) => {
        basicService.redirect('/auth/agents/details/' + e.currentTarget.dataset.id);
    };

    const getComplexlastModifiedCell = (row: IAgentsTableData) => {
        return (
            <TableCell>
                <PasslyTooltip title={translation('AGENT_LAST_MODIFIED_DATE_TOOLTIP')}>
                    <span>{row.lastModified ? passlyFormatDate(row.lastModified, TIME_DATE_FORMAT) : translation('DATE_NEVER')}</span>
                </PasslyTooltip>
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.agentName,
            label: 'AGENT_NAME',
            type: 'link',
            onClick: editRow,
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.agentName,
        },
        {
            id: TableHeadEnum.agentType,
            label: 'AGENT_TYPE'
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'AGENT_STATE',
            type: 'status',
            filterType: FilterEnum.provisioningState,
            suggestionsForFilter: provisionStateSelectData
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'AGENT_LAST_MODIFIED_DATE',
            type: 'getComplexCell',
            getComplexCell: getComplexlastModifiedCell
        }
    ];

    const addNewAgent = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_AGENT'),
            modalSwitcher: modalSwitchState,
            size: 'lg',
            form: <AddAgentDialog startingView={AgentViewType.addAgentListView} pageRefresh={handlePageRefreshFromDialog} />
        });
    };

    const menu: IFabMenu = {
        name: 'NEW_AGENT',
        actions: [
            {
                name: 'NEW_AGENT',
                method: addNewAgent,
                iconElement: <i className='icon icon-auth-agents' />
            }
        ]
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: () => deleteSelected(),
            isDeleteAction: true
        }
    ];

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        if (!loadingData && agents) {
            let newRows = agents.map((agent: IAgentDetail) => {
                const data: IAgentsTableData = {
                    checked: false,
                    agentName: agent.AgentName,
                    agentType: agent.AgentType,
                    provisioningState: agent.ProvisioningState,
                    lastModified: agent.LastModified,
                    id: agent.Id,
                    iconUrl: agent.IconUrl
                };
                return data;
            });

            if (agents.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, agents]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        dispatch(handleGetAgentsData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, organizationFilter, isReset, activeFilters));
    };

    const emptyAgentList = () => {
        return !loadingData && rows.length === 0;
    };

    const deleteSelected = useCallback(() => {
        // loop thru rows.checked
        let ids: string[] = [];

        rows.forEach(function (row) {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        if (ids.length === 0) {
            confirm(translation('SELECT_MORE'), translation('OK'), undefined, '').then(
                () => {
                    // do nothing
                },
                () => {
                    // do nothing on cancel
                }
            );
            return;
        }

        deleteAgentsAreYouSure(ids);
    }, [rows]);

    const deleteAgentsAreYouSure = (ids: any) => {
        confirm(translation('DELETE_AGENT_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_AGENT_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteAgents(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteAgents = (ids: string[]) => {
        if (ids.length > 0) {
            let agents: any = {
                AgentProfiles: ids
            };

            agentsService.deleteAgents(agents, genericErrorToast, handleDeleteSuccess);
        }
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
            dispatch(gridCheckAll(false));
        }
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deleteAgentsAreYouSure(ids);
    };

    return (
        <>
            <ModalComponent {...modalData} />
            {hasOrgPermission([ManageSystem]) && (
                <FabMenu
                    menu={menu}
                    hideTooltip={!emptyAgentList()}
                    tooltipTitle={translation('AUTHAGENTS_NO_AGENTS_TOOL_TIP_TITLE')}
                    tooltipDescription={translation('AUTHAGENTS_NO_AGENTS_TOOL_TIP_DESC')}
                />
            )}

            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={agentsCount}
                onFetch={fetchMoreData}
                withCheckboxes={hasOrgPermission([ManageSystem])}
                actionMenuItems={actionMenuItems}
                emptyListHelper={translation('EMPTY_AGENT_LIST')}
                loadingData={loadingData}
                editRow={editRow}
                deleteRow={deleteRow}
            />
        </>
    );
};

export default AuthAgentsPage;
